import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAsyncData } from "@/hooks/useAsyncData";
import { listAll, removeItem, updateItem } from "@/lib/adminRepository";
import { ADMIN } from "@/constants/testIds";
import { Plus, Pencil, Trash2 } from "lucide-react";

// Dedicated listings manager (MeProperty-style list), themed for A Homely Abode.
export default function ListingsList() {
  const { data: items, loading, error, reload } = useAsyncData(() => listAll("listings"), []);

  const del = async (item) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete "${item.title || item.id}"? This cannot be undone.`)) return;
    try {
      await removeItem("listings", item.id);
      toast.success("Deleted");
      reload();
    } catch {
      toast.error("Delete failed");
    }
  };

  const togglePublish = async (item) => {
    const next = item.status === "published" ? "draft" : "published";
    await updateItem("listings", item.id, { status: next });
    reload();
  };

  return (
    <div data-testid="admin-listings">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="eyebrow text-bronze mb-1">Content</div>
          <h1 className="font-serif text-3xl text-ink">Listings</h1>
        </div>
        <Link to="/admin/listings/new" data-testid={ADMIN.newButton} className="btn-primary inline-flex items-center gap-2">
          <Plus size={16} /> New listing
        </Link>
      </div>

      {loading && <p className="text-olive">Loading…</p>}
      {error && <p className="text-red-700">Failed to load. <button className="underline" onClick={reload}>Retry</button></p>}

      {!loading && !error && (
        <table className="w-full text-sm border border-bronze/20 bg-cream" data-testid={ADMIN.list}>
          <thead className="bg-beige/60 text-left text-[0.65rem] uppercase tracking-wider text-olive">
            <tr>
              <th className="p-3 w-16">Cover</th>
              <th className="p-3">Listing</th>
              <th className="p-3 w-32">Market</th>
              <th className="p-3 w-28">Visibility</th>
              <th className="p-3 w-24">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className="border-t border-bronze/10" data-testid={`admin-row-${item.id}`}>
                <td className="p-2">
                  {item.image ? (
                    <img src={item.image} alt="" className="h-10 w-14 object-cover border border-bronze/20" />
                  ) : (
                    <div className="h-10 w-14 bg-beige border border-bronze/20" />
                  )}
                </td>
                <td className="p-3">
                  <div className="font-medium text-ink">{item.title || item.id}</div>
                  <div className="text-xs text-olive">{item.location || "—"}{item.price ? ` · ${item.price}` : ""}</div>
                </td>
                <td className="p-3 text-xs text-ink_soft">{item.listingStatus || "—"}</td>
                <td className="p-3">
                  <button
                    onClick={() => togglePublish(item)}
                    data-testid={ADMIN.publishToggle}
                    className={`px-2 py-1 text-[0.6rem] uppercase tracking-wider ${item.status === "published" ? "bg-bronze text-cream" : "bg-taupe/40 text-ink"}`}
                  >
                    {item.status || "draft"}
                  </button>
                </td>
                <td className="p-3 flex gap-3">
                  <Link to={`/admin/listings/${item.id}`} className="text-bronze hover:text-bronze_dark" title="Edit"><Pencil size={16} /></Link>
                  <button onClick={() => del(item)} data-testid={ADMIN.deleteButton} className="text-red-600 hover:text-red-800" title="Delete"><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={5} className="p-6 text-center text-olive">No listings yet — click “New listing”.</td></tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}
